import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";
import { Pill, EmptyState, StatCard } from "./AdminUI";

const COLORS = ["#94A3B8", "#2E6B57", "#3B82F6", "#F59E0B", "#EF4444"];

export default function SubscriptionsTab() {
  const [data, setData] = useState(null);
  const [finance, setFinance] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/subscriptions").then(r => setData(r.data));
  useEffect(() => { load(); api.get("/admin/finance").then(r => setFinance(r.data)); }, []);

  const setEnrollment = async (enabled) => {
    if (!window.confirm(enabled
      ? "Set Pro Plus enrollment LIVE? Professionals will be able to subscribe immediately."
      : "PAUSE Pro Plus enrollment? Coming Soon will show everywhere and checkout will be blocked. Existing paid subscribers are NOT affected.")) return;
    setBusy(true);
    try { await api.put("/admin/pro-plus/enrollment", { enabled }); toast.success(enabled ? "Enrollment is LIVE" : "Enrollment paused"); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const setTrialProgram = async (active) => {
    if (!window.confirm(active
      ? "Activate the 14-day trial program? Every currently eligible Free Pro gets their OWN full 14 days, once. This creates no Stripe customers and charges nobody."
      : "Stop offering new 14-day trials? Trials already running keep their original end date.")) return;
    setBusy(true);
    try {
      const { data: r } = await api.put("/admin/pro-plus/trial-program", { active });
      toast.success(active ? `Trial program active${r.trials_granted ? ` — ${r.trials_granted} trial(s) granted` : ""}` : "New trials stopped");
      await load();
    }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  if (!data) return <p className="text-sm text-[#222]/45">Loading…</p>;
  const { summary, pros, program, pricing } = data;

  return (
    <div data-testid="admin-subscriptions-tab">
      <div className="flex items-center gap-2 mb-6 flex-wrap">
        <Pill tone={data.stripe_mode === "live" ? "red" : "amber"}>Stripe: {data.stripe_mode === "live" ? "LIVE mode" : "TEST mode"}</Pill>
        <Pill tone={program?.enrollment_enabled ? "green" : "neutral"}>Enrollment: {program?.enrollment_enabled ? "LIVE" : "PAUSED (Coming Soon)"}</Pill>
        <Pill tone={program?.trial_program_active ? "green" : "neutral"}>14-day trials: {program?.trial_program_active ? "ACTIVE" : "OFF"}</Pill>
      </div>

      {/* Pro Plus program controls */}
      <div className="bg-white rounded-2xl p-5 shadow-sm border border-black/5 mb-8" data-testid="admin-pro-plus-program">
        <h3 className="text-sm font-bold mb-1">Pro Plus Accounts — program controls</h3>
        <p className="text-xs text-[#222]/45 mb-4">
          Price is read live from the Stripe Price{pricing?.available ? ` (${pricing.display}, ${pricing.price_id})` : " — currently unavailable"} and cannot be edited here.
        </p>

        <div className="grid md:grid-cols-2 gap-4">
          <div className="rounded-xl border border-black/10 p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-semibold">Pro Plus enrollment</p>
              <Pill tone={program?.enrollment_enabled ? "green" : "neutral"}>{program?.enrollment_enabled ? "Live" : "Paused"}</Pill>
            </div>
            <p className="text-xs text-[#222]/50 mb-3">
              Paused shows a disabled “Coming Soon” CTA and blocks <code>/payments/checkout</code> with a 403 server-side. Existing paid subscribers are never affected.
            </p>
            <div className="flex gap-2">
              <button onClick={() => setEnrollment(true)} disabled={busy || program?.enrollment_enabled} className="px-4 py-2 rounded-full bg-[#2E6B57] text-white text-sm font-semibold disabled:opacity-40" data-testid="admin-enrollment-live">Set Live</button>
              <button onClick={() => setEnrollment(false)} disabled={busy || !program?.enrollment_enabled} className="px-4 py-2 rounded-full bg-white border border-black/15 text-sm font-semibold disabled:opacity-40" data-testid="admin-enrollment-pause">Pause</button>
            </div>
          </div>

          <div className="rounded-xl border border-black/10 p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-semibold">14-day free trial program</p>
              <Pill tone={program?.trial_program_active ? "green" : "neutral"}>{program?.trial_program_active ? "Active" : "Off"}</Pill>
            </div>
            <p className="text-xs text-[#222]/50 mb-3">
              Each eligible Pro gets their OWN full {program?.trial_days || 14} days, once ever. No card, no Stripe customer, no automatic charge. Cannot start while enrollment is paused.
            </p>
            <div className="flex gap-2">
              <button onClick={() => setTrialProgram(true)} disabled={busy || program?.trial_program_active || !program?.enrollment_enabled} className="px-4 py-2 rounded-full bg-[#2E6B57] text-white text-sm font-semibold disabled:opacity-40" data-testid="admin-trial-activate">Activate</button>
              <button onClick={() => setTrialProgram(false)} disabled={busy || !program?.trial_program_active} className="px-4 py-2 rounded-full bg-white border border-black/15 text-sm font-semibold disabled:opacity-40" data-testid="admin-trial-stop">Stop new trials</button>
            </div>
            {!program?.enrollment_enabled && <p className="text-[11px] text-amber-700 mt-2" data-testid="admin-trial-blocked-note">Enrollment is paused — population time must not count toward anyone's trial.</p>}
          </div>
        </div>
      </div>

      {finance && (
        <div className="mb-8">
          <h3 className="text-sm font-bold mb-3">Finance — real payment data only</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-3">
            <StatCard label="Actual collected" value={`$${(finance.actual_collected_revenue_cents.value / 100).toFixed(2)}`} sub={finance.actual_collected_revenue_cents.formula} testid="fin-actual-revenue" />
            <StatCard label="Estimated MRR" value={finance.estimated_mrr.available ? `$${finance.estimated_mrr.value.toFixed(2)}` : "Not available"} sub={finance.estimated_mrr.formula} testid="fin-mrr" />
            <StatCard label="Past due" value={finance.past_due.value} sub={finance.past_due.formula} testid="fin-past-due" />
            <StatCard label="Canceled" value={finance.canceled.value} sub={finance.canceled.formula} testid="fin-canceled" />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-3">
            <StatCard label="Real Stripe subscribers" value={finance.active_subscription_value.real_stripe_backed_subscribers} testid="fin-real-subs" />
            <StatCard label="Mocked/manual Pro Plus" value={finance.active_subscription_value.mocked_or_manual_pro_plus_accounts} sub="Never counted as revenue" testid="fin-mocked-subs" />
            <StatCard label="Refunded" value={finance.refunded.available ? `$${(finance.refunded.value / 100).toFixed(2)}` : "Not tracked"} sub={finance.refunded.note} testid="fin-refunded" />
            <StatCard label="Failed payments" value={finance.failed_payments.value} sub={finance.failed_payments.formula} testid="fin-failed" />
          </div>
          <p className="text-xs text-[#222]/45">{finance.active_subscription_value.note}</p>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-6">
        <StatCard label="Free Pro" value={summary.free_pro} testid="sub-free-pro" />
        <StatCard label="Pro Plus active" value={summary.pro_plus_active} testid="sub-active" />
        <StatCard label="On 14-day trial" value={summary.on_trial} testid="sub-on-trial" />
        <StatCard label="Trial expired" value={summary.trial_expired} sub="Reverted to Free Pro" testid="sub-trial-expired" />
        <StatCard label="Trialing (Stripe)" value={summary.trialing} testid="sub-trialing" />
        <StatCard label="Past due" value={summary.past_due} testid="sub-past-due" />
        <StatCard label="Canceled" value={summary.canceled} testid="sub-canceled" />
      </div>

      <div className="grid md:grid-cols-2 gap-6 mb-6">
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-black/5">
          <h3 className="text-sm font-bold mb-3">Subscription status</h3>
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie data={data.status_distribution} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={75} label>
                {data.status_distribution.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-black/5">
          <h3 className="text-sm font-bold mb-3">Pro Plus price (from Stripe)</h3>
          {pricing?.available ? (
            <>
              <p className="text-3xl font-bold" data-testid="admin-stripe-price">{pricing.display}</p>
              <p className="text-xs text-[#222]/45 mt-2">
                Read live from Stripe Price <code>{pricing.price_id}</code> ({pricing.amount_exact} {pricing.currency.toUpperCase()} every {pricing.interval_count === 1 ? pricing.interval : `${pricing.interval_count} ${pricing.interval}s`}).
                Single source of truth — change it in Stripe, not here.
              </p>
            </>
          ) : (
            <p className="text-sm text-amber-700" data-testid="admin-stripe-price-unavailable">
              Price unavailable — check <code>STRIPE_PRO_PLUS_PRICE_ID</code>. LIMYA deliberately shows no amount rather than guessing one.
            </p>
          )}
        </div>
      </div>

      {pros.length === 0 ? (
        <EmptyState title="No Pro accounts" body="Pro Plus subscribers will appear here." testid="admin-subscriptions-empty" />
      ) : (
        <div className="bg-white rounded-2xl shadow-sm border border-black/5 overflow-hidden">
          <table className="w-full text-sm" data-testid="admin-subscriptions-table">
            <thead className="bg-black/[0.03] text-[11px] uppercase tracking-wider text-[#222]/50">
              <tr><th className="text-left px-4 py-3">Business</th><th className="text-left px-4 py-3">Status</th><th className="text-left px-4 py-3">Trial start</th><th className="text-left px-4 py-3">Trial end</th><th className="text-left px-4 py-3">Remaining</th><th className="text-left px-4 py-3 hidden md:table-cell">Joined</th><th className="text-right px-4 py-3">Stripe</th></tr>
            </thead>
            <tbody>
              {pros.map(p => (
                <tr key={p.id} className="border-t border-black/5" data-testid={`admin-sub-row-${p.id}`}>
                  <td className="px-4 py-3">{p.business_name || p.name}</td>
                  <td className="px-4 py-3">
                    <Pill tone={p.is_pro_plus ? (p.pro_plus_status === "past_due" ? "amber" : p.pro_plus_status === "canceled" ? "red" : "green") : p.trial?.trial_active ? "blue" : "neutral"}>
                      {p.is_pro_plus ? (p.pro_plus_status || "active") : p.trial?.trial_active ? "Trial" : "Free Pro"}
                    </Pill>
                  </td>
                  <td className="px-4 py-3 text-[#222]/60 text-xs" data-testid={`trial-start-${p.id}`}>{p.trial?.trial_started_at ? new Date(p.trial.trial_started_at).toLocaleDateString() : "—"}</td>
                  <td className="px-4 py-3 text-[#222]/60 text-xs" data-testid={`trial-end-${p.id}`}>{p.trial?.trial_ends_at ? new Date(p.trial.trial_ends_at).toLocaleDateString() : "—"}</td>
                  <td className="px-4 py-3 text-xs" data-testid={`trial-remaining-${p.id}`}>
                    {p.trial?.trial_active
                      ? <span className="text-[#245544] font-semibold">{p.trial.trial_days_remaining}d left</span>
                      : p.trial?.trial_expired ? <span className="text-[#222]/40">Expired</span> : <span className="text-[#222]/30">—</span>}
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell text-[#222]/50">{new Date(p.created_at).toLocaleDateString()}</td>
                  <td className="px-4 py-3 text-right">{p.stripe_customer_url ? <a href={p.stripe_customer_url} target="_blank" rel="noreferrer" className="text-[#245544] text-xs">Open →</a> : <span className="text-[#222]/30 text-xs">—</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
